import { IconCross } from 'assets/icon-cross';
import { Currency } from 'product-modules/domain/product-module-definition-settings';
import React, { useEffect } from 'react';
import { ArrayField, useFieldArray, UseFormMethods } from 'react-hook-form';
import { LinkButton } from 'rootstrap/components-old/buttons';
import { BaseStoreModel, FormStoreModelList } from 'rootstrap/components-old/root-schema-form/interfaces';
import {
  evaluateDisplayConditionsForOutputData,
  getComponent,
} from 'rootstrap/components-old/root-schema-form/root-schema-form';
import { getFormKey } from 'rootstrap/components-old/root-schema-form/utils/output-data';
import { Size, SuccessButton } from 'rootstrap/components/button/styles';
import { Spacing } from 'rootstrap/global-styles';
import { Colors } from 'rootstrap/global-styles/colors';
import { JSONObject } from 'shared/utils';
import { getColor, ProductModuleDefinitionEmbeddedConfig } from 'site-config';
import styled from 'styled-components';
import { StyledDividerOpenColor } from './divider';
import { InputFieldDisplayProperties } from './input-field';
import { getNextComponentId } from './utils';

type FormData = JSONObject;

export const ListField = (params: {
  name: string;
  form: UseFormMethods<JSONObject>;
  secondaryStepStaticData: FormData | undefined;
  componentSchema: FormStoreModelList;
  siteConfig: ProductModuleDefinitionEmbeddedConfig | null;
  onSubmit: (data: any, isLastElement: boolean) => void | Promise<void>;
  currency: Currency | undefined;
  prefillValues: JSONObject | undefined;
  onCompletedActiveComponentName: string;
  displayProperties: InputFieldDisplayProperties;
  isTouched: boolean;
  submitOnChange?: boolean;
  disableSteppedComponents: boolean;
  isSecondaryStep: boolean | undefined;
  disableChangeListItems?: boolean;
  isLastStep: boolean;
  hiddenComponent?: boolean;
}) => {
  const {
    form,
    name,
    componentSchema,
    siteConfig,
    onSubmit,
    currency,
    prefillValues,
    onCompletedActiveComponentName,
    displayProperties,
    isTouched,
    submitOnChange,
    secondaryStepStaticData,
    isSecondaryStep,
    disableSteppedComponents,
    disableChangeListItems,
    isLastStep,
    hiddenComponent,
  } = params;
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name,
  });
  const { activeElement, setActiveElement } = displayProperties;
  const isActive = activeElement.elementId === name;

  // Always ensure there is at least one child component
  useEffect(() => {
    if (!hiddenComponent && fields.length === 0) {
      append({});
    }
  }, [hiddenComponent]);

  useEffect(() => {
    if (!hiddenComponent && isActive) {
      setActiveElement &&
        setActiveElement({
          elementId: `${name}[${0}].${getFormKey(componentSchema.components[0])}`,
        });
    }
  }, [name, isActive, hiddenComponent]);

  const displayAddAndRemove = disableChangeListItems !== undefined ? !disableChangeListItems : !isSecondaryStep;
  const isMaxComponent = componentSchema.maxNumber && fields.length === componentSchema.maxNumber;

  return (
    <div key={name}>
      {!hiddenComponent && <StyledDividerOpenColor height={1} color={Colors.Body} />}
      {fields.map((field, fieldIndex) => {
        const { components, outputPathList } = componentSchema;

        return (
          <div key={field.id}>
            <ListItemWrapper siteConfig={siteConfig} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ flexGrow: 1 }}>
                {!hiddenComponent && displayAddAndRemove && fieldIndex === fields.length - 1 && fields.length !== 1 && (
                  <LinkButton
                    id={`remove-button-${name}-${field.id}`}
                    style={{ float: 'right' }}
                    size={Size.lg}
                    onClick={async () => {
                      remove(fieldIndex);
                      if (await form.trigger()) {
                        const isLastElement = activeElement.elementId === onCompletedActiveComponentName;
                        await form.handleSubmit((formValues) => onSubmit(formValues, isLastElement))();
                      }
                    }}
                  >
                    <span>
                      <span className='remove-icon'>
                        <IconCross size={10} color={getColor({ siteConfig, color: 'primary' })} />
                      </span>
                      Remove
                    </span>
                  </LinkButton>
                )}
                {components.map((componentSchema, index) => {
                  const { key, nextComponentName } = getNextKey({
                    componentSchema,
                    displayProperties,
                    fieldIndex,
                    fields,
                    index,
                    name,
                    components,
                  });

                  const listKey = outputPathList || name;
                  const listData = prefillValues && listKey ? prefillValues[listKey] : undefined;
                  const itemPrefillValues = Array.isArray(listData) ? (listData[fieldIndex] as JSONObject) : undefined;

                  const Component = getComponent({
                    componentSchema: componentSchema as any,
                    arrayIndex: fieldIndex,
                    form,
                    onSubmit,
                    siteConfig,
                    currency,
                    prefillValues: itemPrefillValues,
                    isSecondaryStep,
                    secondaryStepStaticData,
                    submitOnChange: !!submitOnChange,
                    isTouched,
                    isLastStep,
                    disableSteppedComponents,
                    onCompletedActiveComponentName,
                    formName: key,
                    displayProperties: {
                      setActiveElement: displayProperties.setActiveElement,
                      activeElement: displayProperties.activeElement,
                      nextComponentName,
                    } as any,
                  });

                  const formValue = form.watch(`${outputPathList || ''}[${fieldIndex}]`) as JSONObject;
                  const shouldRenderComponent = evaluateDisplayConditionsForOutputData({
                    displayConditions: componentSchema.displayConditions,
                    formData: formValue,
                  });

                  if (!shouldRenderComponent) {
                    form.unregister(key);
                  }

                  return shouldRenderComponent && <div>{Component}</div>;
                })}
              </div>
            </ListItemWrapper>
            {!hiddenComponent && <StyledDividerOpenColor height={1} color={Colors.Body} />}
          </div>
        );
      })}
      {!hiddenComponent && displayAddAndRemove && !isMaxComponent && (
        <>
          <StyledListFieldButtonWrapper>
            <SuccessButton
              outline
              siteConfig={siteConfig}
              id={`add-button-${name}`}
              disabled={false}
              size={Size.lg}
              onClick={async () => {
                append({});
                onAddItemClicked({ activeElementId: activeElement.elementId || '' });

                if (await form.trigger()) {
                  const isLastElement = activeElement.elementId === onCompletedActiveComponentName;

                  await form.handleSubmit((formValues) => onSubmit(formValues, isLastElement))();
                }
              }}
            >
              {componentSchema.props?.addButtonLabel || 'Add new'}
            </SuccessButton>
          </StyledListFieldButtonWrapper>
          <StyledDividerOpenColor height={1} color={Colors.Body} />
        </>
      )}
    </div>
  );
};

const onAddItemClicked = async (params: { activeElementId: string }) => {
  const { activeElementId } = params;
  await new Promise((resolve) => {
    let counter = 0;
    const intervalId = setInterval(() => {
      const currentComponentNextButton = document.getElementById(`stepped-form-next-button-${activeElementId}` || '');
      if (currentComponentNextButton) {
        clearInterval(intervalId);
        currentComponentNextButton.click();
        resolve(currentComponentNextButton);
      } else if (counter >= 10) {
        clearInterval(intervalId);
        resolve(null);
      }
      counter++;
    }, 100);
  });
};

const getNextKey = (params: {
  fields: Partial<ArrayField<Record<string, any>, 'id'>>[];
  fieldIndex: number;
  name: string;
  componentSchema: BaseStoreModel;
  index: number;
  displayProperties: InputFieldDisplayProperties;
  components: BaseStoreModel[];
}) => {
  const { fieldIndex, fields, name, componentSchema, index, displayProperties, components } = params;

  if (Array.isArray(componentSchema.key)) {
    throw new TypeError('Unhandled array key');
  }

  const currentComponentKey = `${name}[${fieldIndex}].${getFormKey(componentSchema)}`;
  const nextKey = getFormKey(components[index + 1]);

  let nextComponentName = '';
  if (nextKey) {
    nextComponentName = `${name}[${fieldIndex}].${nextKey}`;
  } else if (fields[fieldIndex + 1]) {
    nextComponentName = `${name}[${fieldIndex + 1}].${getFormKey(components[0])}`;
  } else {
    nextComponentName = getNextComponentId({ ...displayProperties, key: name });
  }

  return {
    key: currentComponentKey,
    nextComponentName,
  };
};

const StyledListFieldButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 39px;
  margin-bottom: 39px;
`;

const ListItemWrapper = styled.div<{ siteConfig: ProductModuleDefinitionEmbeddedConfig | null }>`
  .field-divider {
    border: 0px;
  }

  .btn-link {
    color: ${({ siteConfig }) => getColor({ siteConfig, color: 'primary' })};
    margin-top: 32px;
    padding: 0;

    span {
      font-size: 14px;
    }

    .remove-icon {
      padding-right: ${Spacing.xs};
    }
  }
`;
